import React from "react";
import toast from "react-hot-toast";

import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import PersonRemoveAlt1Icon from "@mui/icons-material/PersonRemoveAlt1";
import {
  Button,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";

import { AuthContext } from "../../../context";
import { useListUsers, useUpdateUserType } from "../../../store";
import { User, UserType } from "../../../types";
import { UserAvatar } from "../../../components/avatars";

interface ClientAdminUsersTabProps {
  clientId: string;
}

const sortUsers = (a: User, b: User) => {
  const nameA = (a.fullName || a.email || "Unknown user").toLowerCase();
  const nameB = (b.fullName || b.email || "Unknown user").toLowerCase();

  return nameA.localeCompare(nameB);
};

const ClientAdminUsersTab: React.FC<ClientAdminUsersTabProps> = ({
  clientId,
}) => {
  const { userId: currentUserId } = React.useContext(AuthContext);
  const { data: users = [], isLoading } = useListUsers(true, clientId);
  const [selectedUserId, setSelectedUserId] = React.useState("");

  const currentAdmins = React.useMemo(
    () =>
      [...users]
        .filter((user) => user.userType === UserType.SysAdmin)
        .sort(sortUsers),
    [users],
  );

  const availableUsers = React.useMemo(
    () =>
      [...users]
        .filter((user) => user.userType !== UserType.SysAdmin)
        .sort(sortUsers),
    [users],
  );

  const { mutate: updateType, isPending } = useUpdateUserType(
    () => {
      toast.success("Client admin updated");
      setSelectedUserId("");
    },
    (error: unknown) => {
      console.error(error);
      toast.error("Failed to update client admin");
    },
  );

  const handleAddAdmin = () => {
    if (!selectedUserId) {
      return;
    }

    const selectedUser = users.find((user) => user.userId === selectedUserId);
    if (!selectedUser) {
      return;
    }

    updateType({
      targetId: selectedUserId,
      userType: UserType.SysAdmin,
    });
  };

  const handleRemoveAdmin = (user: User) => {
    if (currentAdmins.length <= 1) {
      toast.error("At least one client admin must remain for this client.");
      return;
    }

    if (user.userId === currentUserId) {
      toast.error("You cannot remove your own admin access from this client.");
      return;
    }

    updateType({
      targetId: user.userId,
      userType: UserType.User,
    });
  };

  return (
    <Stack spacing={2} sx={{ px: 2, pb: 2 }}>
      <Typography variant="body2" color="text.secondary">
        Manage which users can act as client admins for this client. Client
        admins can be added from the user list below and removed at any time.
      </Typography>

      <Stack spacing={1}>
        <Typography variant="subtitle2" fontWeight={700}>
          Current client admins
        </Typography>

        {isLoading ? (
          <Typography color="text.secondary">Loading users...</Typography>
        ) : currentAdmins.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography color="text.secondary">
              No client admins have been assigned yet.
            </Typography>
          </Paper>
        ) : (
          <Stack spacing={1}>
            {currentAdmins.map((user) => {
              const displayName = user.fullName || user.email || "Unknown user";
              const initials =
                displayName
                  .split(" ")
                  .filter(Boolean)
                  .slice(0, 2)
                  .map((part) => part[0]?.toUpperCase() || "")
                  .join("") || "U";

              const isRemoveDisabled =
                isPending ||
                currentAdmins.length <= 1 ||
                user.userId === currentUserId;
              const removeTooltip =
                currentAdmins.length <= 1
                  ? "At least one client admin is required"
                  : user.userId === currentUserId
                    ? "You cannot remove yourself"
                    : "Remove client admin";

              return (
                <Paper
                  key={user.userId}
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <UserAvatar text={displayName} color="#055480" />
                    <Stack sx={{ minWidth: 0, gap: 0.25 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 700,
                          lineHeight: 1.2,
                          fontSize: "1rem",
                        }}
                        noWrap
                      >
                        {displayName}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: "0.9rem", lineHeight: 1 }}
                        noWrap
                      >
                        {user.email}
                      </Typography>
                    </Stack>
                  </Stack>

                  <Tooltip title={removeTooltip}>
                    <span>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveAdmin(user)}
                        disabled={isRemoveDisabled}
                        aria-label={`Remove ${displayName} as client admin`}
                      >
                        <PersonRemoveAlt1Icon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Paper>
              );
            })}
          </Stack>
        )}
      </Stack>

      <Divider sx={{ my: 2 }} />

      <Stack spacing={1}>
        <FormControl fullWidth size="small">
          <InputLabel id="client-admin-select-label">Select user</InputLabel>
          <Select
            labelId="client-admin-select-label"
            id="client-admin-select"
            value={selectedUserId}
            label="Select user"
            onChange={(event) => setSelectedUserId(event.target.value)}
            disabled={availableUsers.length === 0 || isPending}
          >
            {availableUsers.length === 0 ? (
              <MenuItem value="" disabled>
                No users available to add
              </MenuItem>
            ) : (
              availableUsers.map((user) => {
                const displayName =
                  user.fullName || user.email || "Unknown user";

                return (
                  <MenuItem key={user.userId} value={user.userId}>
                    <Stack
                      direction="row"
                      spacing={1.5}
                      alignItems="center"
                      sx={{ width: "100%" }}
                    >
                      <UserAvatar text={displayName} color="#055480" />
                      <ListItemText
                        primary={displayName}
                        secondary={user.email}
                      />
                    </Stack>
                  </MenuItem>
                );
              })
            )}
          </Select>
        </FormControl>

        <Button
          variant="contained"
          startIcon={<PersonAddAlt1Icon />}
          onClick={handleAddAdmin}
          disabled={isPending || !selectedUserId}
        >
          {isPending ? "Saving..." : "Add as client admin"}
        </Button>
      </Stack>
    </Stack>
  );
};

export default ClientAdminUsersTab;
